import React, { Suspense, lazy, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Button } from "@radix-ui/themes";
import { ChevronDown, Menu, X } from "lucide-react";
import type { User } from "firebase/auth";
import "./Header.css";
import MobileMenu, { SITE_MOBILE_NAV_PANEL_ID } from "./MobileMenu";
import HeaderSearch, { HeaderSearchSheet, HeaderSearchTrigger } from "./HeaderSearch";
import { BrandLogo } from "@/components/BrandLogo";
import { useEventsFeature } from "@/contexts/EventsFeatureContext";
import { useOptionalSubscription } from "@/components/User/SubscriptionContext";
import {
  getCurrentAuthUser,
  subscribeToAuthState,
} from "@/firebase/auth-state";
import { MEMBERSHIP_PATH } from "@/constants/membershipCopy";
import { PRODUCT_DISPLAY_NAME } from '@/utils/seoUtils';
import {
  getForecastMenuItems,
  isSiteNavSectionActive,
  type SiteNavSection,
} from "./siteNavItems";

const LoginButton = lazy(() => import("../../../Auth/LoginButton"));

const SEARCH_SHEET_ID = "site-header-search-sheet";

interface HeaderProps {
  showBackButton?: boolean;
  onBackClick?: () => void;
}

const Header: React.FC<HeaderProps> = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [user, setUser] = useState<User | null>(() => getCurrentAuthUser());
  const subscription = useOptionalSubscription();
  const isPremiumUser = subscription?.isPremiumUser ?? false;
  const {
    ready: eventsFeatureReady,
    forecastEnabled,
  } = useEventsFeature();
  const forecastItems = getForecastMenuItems(forecastEnabled);

  useEffect(() => {
    return subscribeToAuthState((nextUser) => setUser(nextUser));
  }, []);

  useEffect(() => {
    setIsSearchOpen(false);
  }, [pathname]);

  const handleLogoClick = () => {
    navigate(user ? "/maps" : "/");
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsSearchOpen(false);
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const openSearch = () => {
    setIsMenuOpen(false);
    setIsSearchOpen(true);
  };

  const linkClass = (section: SiteNavSection) =>
    isSiteNavSectionActive(pathname, section) ? "nav-link nav-link--current" : "nav-link";
  const ariaCurrent = (section: SiteNavSection) =>
    isSiteNavSectionActive(pathname, section) ? ("page" as const) : undefined;

  // Signed-in non-members get Join; guests keep Log in / Sign up from LoginButton.
  const showJoin = Boolean(user) && !isPremiumUser && pathname !== MEMBERSHIP_PATH;

  return (
    <nav className="nav">
      <div className="nav-content">
        <div className="nav-brand-cluster">
          <button
            type="button"
            className="nav-brand"
            onClick={handleLogoClick}
            aria-label={`${PRODUCT_DISPLAY_NAME} home`}
          >
            <BrandLogo className="nav-brand-logo" alt="" aria-hidden />
          </button>
        </div>

        <HeaderSearch />

        <div className="nav-trailing">
          <div className="nav-menu desktop-menu">
            <button
              type="button"
              className={linkClass("map")}
              aria-current={ariaCurrent("map")}
              onClick={() => handleMenuClick("/maps")}
            >
              Map
            </button>
            <button
              type="button"
              className={linkClass("field-guide")}
              aria-current={ariaCurrent("field-guide")}
              onClick={() => handleMenuClick("/field-guide")}
            >
              Field Guide
            </button>
            {eventsFeatureReady ? (
              <button
                type="button"
                className={linkClass("experiences")}
                aria-current={ariaCurrent("experiences")}
                onClick={() => handleMenuClick("/events")}
              >
                Experiences
              </button>
            ) : null}
            <DropdownMenu.Root modal={false}>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className={`${linkClass("forecasts")} nav-link--menu`}
                >
                  Forecasts
                  <ChevronDown size={13} strokeWidth={2.2} aria-hidden />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="site-header-dropdown-content nav-forecast-menu"
                  sideOffset={8}
                  align="start"
                >
                  {forecastItems.map((item) => (
                    <DropdownMenu.Item
                      key={item.path}
                      className={`site-header-dropdown-item${
                        isSiteNavSectionActive(pathname, item.section, item.path)
                          ? " nav-forecast-menu__item--current"
                          : ""
                      }`}
                      onSelect={() => handleMenuClick(item.path)}
                    >
                      {item.label}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {showJoin ? (
            <button
              type="button"
              className="nav-join desktop-menu"
              onClick={() => handleMenuClick(MEMBERSHIP_PATH)}
            >
              Join
            </button>
          ) : null}

          <div className="nav-auth">
            <Suspense
              fallback={
                <div
                  style={{
                    minWidth: 40,
                    minHeight: 32,
                    visibility: "hidden",
                  }}
                  aria-hidden="true"
                />
              }
            >
              <LoginButton siteHeader />
            </Suspense>
          </div>
          <HeaderSearchTrigger
            onOpen={openSearch}
            expanded={isSearchOpen}
            controls={SEARCH_SHEET_ID}
          />
          <Button
            variant="ghost"
            onClick={toggleMenu}
            className="mobile-menu-button"
            type="button"
            aria-expanded={isMenuOpen}
            aria-controls={SITE_MOBILE_NAV_PANEL_ID}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? (
              <X size={22} strokeWidth={2} />
            ) : (
              <Menu size={22} strokeWidth={2} />
            )}
          </Button>
        </div>
      </div>

      <MobileMenu isOpen={isMenuOpen} onClose={closeMenu} />
      {isSearchOpen ? (
        <HeaderSearchSheet id={SEARCH_SHEET_ID} onClose={() => setIsSearchOpen(false)} />
      ) : null}
    </nav>
  );
};

export default Header;
